<template>
  <div class="container">
    <div class="row">
      <div class="col-lg-12">
        <h1 class="page-header">Organisationsuppgifter (steg 1 av 3)</h1>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-8">
        <p>Ange organisationsnummer och hämta uppgifter. Om uppgifter saknas får du möjlighet att ange dem.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6">
        <form id="registration-form" class="reg-form" @submit.prevent="onSubmit">
          <div class="form-group reg-form__wrapper my-3 form-background-color">
            <h3 class="h4 mt-0">Ange uppgifter om organisation</h3>
            <div class="form-group">
              <label class="form-check-label--normal" for="orgNo">Organisationsnummer (XXXXXX-XXXX)</label>
              <input id="orgNo" v-model="form.orgNo" :disabled="state.step > 0"
                     aria-errormessage="validation-error-orgNo" class="form-control" maxlength="11"
                     name="orgNo" placeholder="XXXXXX-XXXX" required
                     type="text" @input="maskOrgNo(form.orgNo)">
              <div v-if="!state.orgNumberValid" id="validation-error-orgNo"
                   class="d-flex rounded mt-2 p-2 invalid-input">
                <div class="mr-2">
                  <svg aria-hidden="true" aria-label="triangle-exclamation" class="svg-icon triangle-exclamation"
                       data-v-d85c0ce2=""
                       height="512" style="transform: rotate(0deg);" viewBox="0 0 512 512"
                       width="512" xmlns="http://www.w3.org/2000/svg">
                    <g class="svg__color" fill="currentColor" fill-rule="evenodd">
                      <triangleExclamation />
                    </g>
                  </svg>
                </div>
                <p>
                  {{ state.errorText }}
                </p>
              </div>
            </div>
            <div v-if="state.step === 1">
              <div class="form-group">
                <label class="form-check-label--normal" for="displayName">Organisationsnamn *</label>
                <input id="displayName" v-model="form.displayName" aria-errormessage="validation-error-displayName"
                       aria-required="true" class="form-control"
                       name="displayName" placeholder="" type="text">
                <div v-if="state.displayNameError" id="validation-error-displayName"
                     class="d-flex rounded mt-2 p-2 invalid-input">
                  <p>
                    {{ state.displayNameError }}
                  </p>
                </div>
              </div>
              <div class="form-group">
                <label class="form-check-label--normal" for="streetAddress">Postadress</label>
                <input id="streetAddress" v-model="form.streetAddress" class="form-control" name="streetAddress"
                       placeholder=""
                       type="text">
              </div>
              <div class="form-group">
                <label class="form-check-label--normal" for="postalCode">Postnummer *</label>
                <input id="postalCode" v-model="form.postalCode" aria-errormessage="validation-error-postalCode"
                       class="form-control" min="0"
                       name="postalCode" onkeydown="return event.keyCode !== 69" placeholder="" step="1">
                <div v-if="state.postalCodeError" id="validation-error-postalCode"
                     class="d-flex rounded mt-2 p-2 invalid-input">
                  <p>
                    {{ state.postalCodeError }}
                  </p>
                </div>
              </div>
              <div class="form-group">
                <label class="form-check-label--normal" for="locality">Ort *</label>
                <input id="locality" v-model="form.locality" aria-errormessage="validation-error-locality"
                       aria-required="true" class="form-control"
                       name="locality" placeholder="" type="text">
                <div v-if="state.localityError" id="validation-error-locality"
                     class="d-flex rounded mt-2 p-2 invalid-input">
                  <p>
                    {{ state.localityError }}
                  </p>
                </div>
              </div>
              <div class="form-group">
                <label class="form-check-label--normal" for="phoneNumber">Telefonnummer</label>
                <input id="phoneNumber" v-model="form.phoneNumber" aria-errormessage="validation-error-phoneNumber"
                       class="form-control" min="0"
                       name="phoneNumber" onkeydown="return event.keyCode !== 69" placeholder="" step="1">
                <div v-if="state.phoneNumberError" id="validation-error-phoneNumber"
                     class="d-flex rounded mt-2 p-2 invalid-input">
                  <div class="mr-2">
                    <svg aria-hidden="true" aria-label="triangle-exclamation" class="svg-icon triangle-exclamation"
                         data-v-d85c0ce2=""
                         height="512" style="transform: rotate(0deg);" viewBox="0 0 512 512"
                         width="512" xmlns="http://www.w3.org/2000/svg">
                      <g class="svg__color" fill="currentColor" fill-rule="evenodd">
                        <triangleExclamation />
                      </g>
                    </svg>
                  </div>
                  <p>
                    {{ state.phoneNumberError }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <CtaPanel>
            <div class="buttons-container spread-buttons">
              <PreviousNavLink go-to-link="/"
                               previous-label="Avbryt"></PreviousNavLink>
              <button class="btn btn-light" type="submit">
                {{ state.step === 0 ? "Hämta uppgifter" : "Nästa steg" }}<span
                v-if="state.step !== 0" class="ml-2 fas fa-chevron-right"></span></button>
            </div>
          </CtaPanel>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { validateFields, validateOrgNr } from "@/lib/validators";
import { useUserStore } from "@/stores/user";
import { defineComponent, reactive, ref } from "@vue/runtime-core";
import { useRouter } from "vue-router";
import { useOrganisationStore } from "@/stores/organisation";
import CtaPanel from "@/components/CtaPanel.vue";
import { AddressModel } from "@Shared/Models/AddressModel";
import triangleExclamation from "@/components/svg/triangle-exclamation.vue";
import PreviousNavLink from "@/components/PreviousNavLink.vue";

export default defineComponent({

  name: "RegistrationFormStep1",
  components: {
    CtaPanel,
    triangleExclamation,
    PreviousNavLink
  },
  setup() {
    const state = reactive({
      orgNumber: null,
      orgNumberExist: false,
      orgNumberValid: true,
      phoneNumberError: "",
      displayNameError: "",
      postalCodeError: "",
      localityError: "",
      errorText: "",
      step: 0
    });

    const OrganisationStore = useOrganisationStore();
    const UserStore = useUserStore();
    const router = useRouter();
    sessionStorage.removeItem("organisation");
    sessionStorage.removeItem("user");

    const form = ref({
      orgNo: "",
      displayName: "",
      streetAddress: "",
      postalCode: "",
      locality: "",
      phoneNumber: ""
    });

    const onSubmit = async () => {
      if (state.step === 0) {
        if (validateOrgNr(form.value.orgNo)) {
          const result = await OrganisationStore.SearchByOrgNr(form.value.orgNo);
          if (result.errorMsg.length === 0) {
            state.orgNumberValid = true;
            state.step = 1;
            form.value.displayName = result.displayName;
            form.value.phoneNumber = result.phoneNumber;

            if (result.addressList && result.addressList.length > 0) {
              form.value.locality = result.addressList[0].locality;
              form.value.postalCode = result.addressList[0].postalCode;
              form.value.streetAddress = result.addressList[0].streetAddress ?? "";
            }
          } else {
            state.orgNumberValid = false;
            state.errorText = result.errorMsg;
          }
        } else {
          state.orgNumberValid = false;
          state.errorText = "Detta verkar inte vara ett korrekt organisationsnummer, var vänlig kolla igen eller kontakta supporten om problemet kvarstår";
        }
      } else if (state.step === 1) {
        // Namn, korrekt orgnr, OrganisationType=Head, minst 1 st contactinfolist med typen Head och organisationstatusType=Created
        let data = OrganisationStore.organisation;
        const contactInfo = UserStore.getUser();
        if (!data.contactInfoList) {
          data.contactInfoList = [];
        }
        if (!contactInfo.info) {
          contactInfo.info = "";
        }
        if (!contactInfo.phone) {
          contactInfo.phone = "";
        }

        data.contactInfoList.push(contactInfo);

        if (!data.organisationNumber) {
          data.organisationNumber = form.value.orgNo ?? "";
        }
        data.displayName = form.value.displayName;
        data.phoneNumber = form.value.phoneNumber;
        let address: AddressModel = {
          id: 0,
          locality: form.value.locality,
          postalCode: form.value.postalCode,
          streetAddress: form.value.streetAddress,
          countryCode: 0,
          countryName: "",
          municipalityCode: 0,
          municipalityName: "",
          country: "",
          comment: "",
          type: "VisitingAddress"
        };
        if (!data.addressList) {
          data.addressList = [];
          data.addressList.push(address);
        } else {
          data.addressList[0].locality = form.value.locality;
          data.addressList[0].postalCode = form.value.postalCode;
          data.addressList[0].streetAddress = form.value.streetAddress;
          data.addressList[0].type = "VisitingAddress";
        }

        //validate org inputs
        let validateResults = validateFields(data);
        if (validateResults.length) {
          state.displayNameError = validateResults.find(x => x.errorField == "displayName")?.message ?? "";
          state.postalCodeError = validateResults.find(x => x.errorField == "postalCode")?.message ?? "";
          state.localityError = validateResults.find(x => x.errorField == "locality")?.message ?? "";
          state.phoneNumberError = validateResults.find(x => x.errorField == "phoneNumber")?.message ?? "";
        } else {
          sessionStorage.setItem("organisation", JSON.stringify(data));
          sessionStorage.setItem("user", JSON.stringify(UserStore.getUser()));
          OrganisationStore.$patch({ organisation: data });
          await router.push("steg-2");
        }
      }
    };

    const maskOrgNo = (orgNo) => {
      let maskedValue = orgNo.split("-").join("");
      if (maskedValue.length > 0) {
        maskedValue = maskedValue.match(new RegExp(".{1,6}", "g")).join("-");
      }
      form.value.orgNo = maskedValue;
    };

    return {
      state,
      form,
      onSubmit,
      maskOrgNo
    };
  }
});
</script>

<style lang="scss" scoped>
.reg-form {
  &__wrapper {
    padding: 1rem;
    border-radius: 0.2rem;
  }

  .org-information-list {
    display: grid;
    grid-template-columns: 6rem auto;

    &:first-child {
      font-weight: bold;
    }

    &__item {
    }
  }

  .svg-icon {
    display: inline-block;
    vertical-align: baseline;
    pointer-events: none;
    width: 1rem;
    height: auto;

    &.user_plus-icon {
      width: 1.4rem;
    }

    &.star-icon {
      width: 2rem;
    }
  }
}

.invalid-input {
  background-color: var(--spsm-red-5);
}
</style>